import React, { useState, useEffect, useCallback } from 'react';
import { Layout } from '../../components/Layout';
import { useAuth } from '../../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Badge } from '../../components/ui/badge';
import { Activity, Mouse, Keyboard } from 'lucide-react';
import { ActivityBar, formatTrackedTime } from '../../components/activity/ActivityBar';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function ActivityMonitor() {
  const { token } = useAuth();
  const [activityData, setActivityData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [period, setPeriod] = useState('today');

  const fetchActivity = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    try {
      const now = new Date();
      let start, end;
      switch (period) {
        case 'today':
          start = new Date(now.getFullYear(), now.getMonth(), now.getDate());
          end = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59);
          break;
        case 'yesterday': {
          const y = new Date(now);
          y.setDate(y.getDate() - 1);
          start = new Date(y.getFullYear(), y.getMonth(), y.getDate());
          end = new Date(y.getFullYear(), y.getMonth(), y.getDate(), 23, 59, 59);
          break;
        }
        case 'week': {
          const d = now.getDay();
          const diff = d === 0 ? 6 : d - 1;
          start = new Date(now);
          start.setDate(now.getDate() - diff);
          start.setHours(0, 0, 0, 0);
          end = new Date(start);
          end.setDate(start.getDate() + 6);
          end.setHours(23, 59, 59, 999);
          break;
        }
        case 'month':
          start = new Date(now.getFullYear(), now.getMonth(), 1);
          end = new Date(now.getFullYear(), now.getMonth() + 1, 0, 23, 59, 59);
          break;
        default:
          start = new Date(now.getFullYear(), now.getMonth(), now.getDate());
          end = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59);
      }

      const res = await fetch(
        `${API_URL}/api/activity/team-summary?start_date=${start.toISOString()}&end_date=${end.toISOString()}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (res.ok) {
        setActivityData(await res.json());
      }
    } catch (error) {
      console.error('Failed to fetch activity:', error);
    } finally {
      setLoading(false);
    }
  }, [token, period]);

  useEffect(() => {
    fetchActivity();
  }, [fetchActivity]);

  const members = activityData?.members || [];
  const withData = members.filter(m => m.total_seconds > 0);
  const avgActivity = withData.length > 0
    ? Math.round(withData.reduce((s, m) => s + m.overall_percent, 0) / withData.length)
    : 0;
  const totalTracked = members.reduce((s, m) => s + m.total_seconds, 0);

  return (
    <Layout>
      <div className="space-y-6" data-testid="activity-monitor-page">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-slate-900 dark:text-white">Activity Monitor</h1>
            <p className="text-sm text-slate-500 mt-1">Track mouse and keyboard activity across your team</p>
          </div>
          <Select value={period} onValueChange={setPeriod} data-testid="activity-period-select">
            <SelectTrigger className="w-[160px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="today">Today</SelectItem>
              <SelectItem value="yesterday">Yesterday</SelectItem>
              <SelectItem value="week">This Week</SelectItem>
              <SelectItem value="month">This Month</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <Card>
            <CardContent className="p-4">
              <div className="text-sm text-slate-500">Team Avg Activity</div>
              <div className="text-2xl font-bold mt-1" data-testid="team-avg-activity">{avgActivity}%</div>
              <ActivityBar overall={avgActivity} mouse={0} keyboard={0} showBreakdown={false} size="sm" className="mt-2" />
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="text-sm text-slate-500">Members Tracked</div>
              <div className="text-2xl font-bold mt-1" data-testid="members-tracked">{withData.length}/{members.length}</div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="text-sm text-slate-500">Total Tracked</div>
              <div className="text-2xl font-bold mt-1">{formatTrackedTime(totalTracked)}</div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="text-sm text-slate-500">High Activity (70%+)</div>
              <div className="text-2xl font-bold mt-1 text-emerald-600">{withData.filter(m => m.overall_percent >= 70).length}</div>
            </CardContent>
          </Card>
        </div>

        {/* Team Activity Table */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-lg flex items-center gap-2">
              <Activity className="w-5 h-5" /> Team Activity Breakdown
            </CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="py-8 text-center text-slate-400">Loading...</div>
            ) : members.length > 0 ? (
              <div className="overflow-x-auto">
                <table className="w-full text-sm" data-testid="activity-team-table">
                  <thead>
                    <tr className="border-b border-slate-200 dark:border-slate-700">
                      <th className="text-left py-3 px-3 font-medium">Member</th>
                      <th className="text-left py-3 px-3 font-medium">Role</th>
                      <th className="text-left py-3 px-3 font-medium">Tracked Time</th>
                      <th className="text-left py-3 px-3 font-medium min-w-[200px]">Activity Level</th>
                      <th className="text-center py-3 px-3 font-medium">
                        <span className="flex items-center justify-center gap-1"><Mouse className="w-3.5 h-3.5" /> Mouse</span>
                      </th>
                      <th className="text-center py-3 px-3 font-medium">
                        <span className="flex items-center justify-center gap-1"><Keyboard className="w-3.5 h-3.5" /> Keyboard</span>
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {members
                      .sort((a, b) => b.overall_percent - a.overall_percent)
                      .map((member) => (
                      <tr key={member.user_id} className="border-b border-slate-100 dark:border-slate-800 hover:bg-slate-50 dark:hover:bg-slate-800/50" data-testid={`activity-row-${member.user_id}`}>
                        <td className="py-3 px-3">
                          <div className="font-medium flex items-center gap-2">
                            {member.name}
                            {member.agent_capture_status === 'capture_broken' && (
                              <span
                                title={`Agent heartbeat is fresh but window-capture pipeline appears broken. Last successful capture flush: ${member.last_usage_flush_at ? new Date(member.last_usage_flush_at).toLocaleString() : 'never'}. The employee may need to restart the desktop agent.`}
                                className="inline-flex items-center gap-1 text-[10px] font-semibold px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300"
                                data-testid={`agent-health-warn-${member.user_id}`}
                              >
                                ⚠ Capture stale
                              </span>
                            )}
                          </div>
                          <div className="text-xs text-slate-500">{member.email}</div>
                        </td>
                        <td className="py-3 px-3">
                          <Badge variant="outline" className="capitalize text-xs">{member.role}</Badge>
                        </td>
                        <td className="py-3 px-3 font-medium tabular-nums">
                          {formatTrackedTime(member.total_seconds)}
                        </td>
                        <td className="py-3 px-3">
                          {member.total_seconds > 0 ? (
                            <ActivityBar overall={member.overall_percent} mouse={member.mouse_percent} keyboard={member.keyboard_percent} showBreakdown={false} />
                          ) : (
                            <span className="text-slate-400 text-xs">No data</span>
                          )}
                        </td>
                        <td className="py-3 px-3 text-center tabular-nums">{member.mouse_percent}%</td>
                        <td className="py-3 px-3 text-center tabular-nums">{member.keyboard_percent}%</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="py-8 text-center text-slate-500">
                <Activity className="w-12 h-12 mx-auto mb-3 opacity-30" />
                <p>No activity data available for this period.</p>
                <p className="text-xs mt-1">Activity tracking requires the desktop agent to be running.</p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
}
